import { ChangeDetectionStrategy, Component, HostListener, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

/** One vendor-uploaded proof photo (BookingCompletionProof row, mapped for display). */
export interface ProofPhoto {
  id: number;
  /** base64 data-URL stored inline on the row. */
  imageUrl: string;
  caption: string;
}

/**
 * The vendor's "the event happened" photos, shown read-only.
 *
 * Three screens need the same grid — the vendor's own booking detail, the
 * customer's confirm-or-report card, and the admin dispute queue — and they must
 * agree, because the customer releases money on the strength of what they see
 * here and the admin decides a dispute on it. One component, three callers.
 *
 * Tap a thumbnail for a full-size lightbox. `compact` shrinks the grid for the
 * admin queue, where the gallery sits inside an already-dense row.
 */
@Component({
  selector: 'app-proof-gallery',
  standalone: true,
  styleUrl: './proof-gallery.component.scss',
  imports: [CommonModule],
  templateUrl: './proof-gallery.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProofGalleryComponent {
  @Input() photos: ProofPhoto[] = [];
  /** Shown instead of the grid when there is nothing to display. Blank = render nothing. */
  @Input() emptyText = '';
  /** Denser thumbnails (admin action queue). */
  @Input() compact = false;
  /** Optional heading above the grid, e.g. "Proof of completion". */
  @Input() label = '';

  /** Index of the photo open in the lightbox, or -1 for closed. */
  readonly zoomIdx = signal(-1);

  open(i: number): void { this.zoomIdx.set(i); }
  close(): void { this.zoomIdx.set(-1); }

  prev(): void {
    const n = this.photos.length;
    if (n > 1) this.zoomIdx.set((this.zoomIdx() - 1 + n) % n);
  }

  next(): void {
    const n = this.photos.length;
    if (n > 1) this.zoomIdx.set((this.zoomIdx() + 1) % n);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { if (this.zoomIdx() >= 0) this.close(); }

  @HostListener('document:keydown.arrowleft')
  onLeft(): void { if (this.zoomIdx() >= 0) this.prev(); }

  @HostListener('document:keydown.arrowright')
  onRight(): void { if (this.zoomIdx() >= 0) this.next(); }

  trackPhoto = (_: number, p: ProofPhoto): number => p.id;
}
